import { useCallback } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Separator,
} from '@chakra-ui/react'
import {
  X,
  ChevronUp,
  ChevronDown,
  Truck,
  Mail,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  FileText,
  User,
  Clock,
} from 'lucide-react'
import { useSupplier } from '../hooks'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import type { VatRegime, SupplierPaymentMode } from '../types'
import { VAT_REGIME_LABELS, SUPPLIER_PAYMENT_MODE_LABELS } from '../types'

interface SupplierDetailDrawerProps {
  supplierId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function SupplierDetailDrawer({
  supplierId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: SupplierDetailDrawerProps) {
  const { data: supplier, isLoading, error } = useSupplier(supplierId ?? undefined)

  const handleInteractOutside = useCallback(
    (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
      const target = e.detail.originalEvent.target as HTMLElement
      const tableRow = target.closest('[data-supplier-row]') as HTMLElement
      if (tableRow) return
      onClose()
    },
    [onClose]
  )

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="md"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="600px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {supplier && (
                  <>
                    <HStack gap={2} mb={1}>
                      <Badge variant="outline" size="sm" fontFamily="mono">
                        {supplier.code}
                      </Badge>
                      <Badge colorPalette={supplier.is_active ? 'green' : 'gray'} size="sm">
                        {supplier.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {supplier.name}
                    </Heading>
                    {supplier.trade_name && (
                      <Text fontSize="sm" color="gray.500">
                        {supplier.trade_name}
                      </Text>
                    )}
                  </>
                )}
                {!supplier && <Heading size="md">Détail du fournisseur</Heading>}
              </Box>

              {/* Close */}
              <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                <X size={18} />
              </IconButton>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Fournisseur non trouvé" />}

            {supplier && (
              <VStack align="stretch" gap={0} flex={1} height="100%" overflow="auto">
                {/* Informations principales */}
                <HStack
                  gap={4}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="blue.500">
                      <Truck size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Catégorie
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {supplier.supplier_categories?.name || 'Non classé'}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="purple.500">
                      <FileText size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Régime TVA
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {VAT_REGIME_LABELS[supplier.vat_regime as VatRegime]}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="green.500">
                      <CreditCard size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Mode de paiement
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {supplier.payment_mode
                          ? SUPPLIER_PAYMENT_MODE_LABELS[supplier.payment_mode as SupplierPaymentMode]
                          : '-'}
                      </Text>
                    </VStack>
                  </HStack>
                </HStack>

                {/* Informations légales */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack>
                        <Building2 size={16} />
                        <Heading size="sm">Informations légales</Heading>
                      </HStack>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        {supplier.legal_form && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Forme juridique
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {supplier.legal_form}
                            </Text>
                          </HStack>
                        )}
                        {supplier.siret && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              SIRET
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" fontFamily="mono">
                              {supplier.siret}
                            </Text>
                          </HStack>
                        )}
                        {supplier.vat_number && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              N° TVA
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" fontFamily="mono">
                              {supplier.vat_number}
                            </Text>
                          </HStack>
                        )}
                        {supplier.payment_delay && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Délai de paiement
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {supplier.payment_delay} jours
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Coordonnées */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack>
                        <MapPin size={16} />
                        <Heading size="sm">Coordonnées</Heading>
                      </HStack>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        {(supplier.address_line1 || supplier.city) && (
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" color="gray.600">
                              Adresse
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {supplier.address_line1}
                            </Text>
                            {supplier.address_line2 && (
                              <Text fontSize="sm">{supplier.address_line2}</Text>
                            )}
                            <Text fontSize="sm">
                              {supplier.postal_code} {supplier.city}
                            </Text>
                          </VStack>
                        )}

                        <Separator />

                        {supplier.email && (
                          <HStack gap={2}>
                            <Mail size={14} color="var(--chakra-colors-gray-400)" />
                            <Text fontSize="sm">{supplier.email}</Text>
                          </HStack>
                        )}
                        {supplier.phone && (
                          <HStack gap={2}>
                            <Phone size={14} color="var(--chakra-colors-gray-400)" />
                            <Text fontSize="sm">{supplier.phone}</Text>
                          </HStack>
                        )}
                        {supplier.website && (
                          <HStack gap={2}>
                            <Building2 size={14} color="var(--chakra-colors-gray-400)" />
                            <Text fontSize="sm" color="blue.500">
                              {supplier.website}
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Coordonnées bancaires */}
                {(supplier.iban || supplier.bank_name) && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <CreditCard size={16} />
                          <Heading size="sm">Coordonnées bancaires</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <VStack align="stretch" gap={3}>
                          {supplier.bank_name && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Banque
                              </Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {supplier.bank_name}
                              </Text>
                            </HStack>
                          )}
                          {supplier.iban && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                IBAN
                              </Text>
                              <Text fontSize="sm" fontWeight="medium" fontFamily="mono">
                                {supplier.iban}
                              </Text>
                            </HStack>
                          )}
                          {supplier.bic && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                BIC
                              </Text>
                              <Text fontSize="sm" fontWeight="medium" fontFamily="mono">
                                {supplier.bic}
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Contacts */}
                {supplier.supplier_contacts && supplier.supplier_contacts.length > 0 && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack justify="space-between">
                          <HStack>
                            <User size={16} />
                            <Heading size="sm">Contacts</Heading>
                          </HStack>
                          <Badge size="sm">{supplier.supplier_contacts.length}</Badge>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <VStack align="stretch" gap={3}>
                          {supplier.supplier_contacts.map((contact, index) => (
                            <Box key={contact.id}>
                              {index > 0 && <Separator mb={3} />}
                              <VStack align="flex-start" gap={1}>
                                <HStack>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {contact.civility} {contact.first_name} {contact.last_name}
                                  </Text>
                                  {contact.is_primary && (
                                    <Badge colorPalette="blue" size="sm">
                                      Principal
                                    </Badge>
                                  )}
                                </HStack>
                                {contact.function && (
                                  <Text fontSize="xs" color="gray.500">
                                    {contact.function}
                                  </Text>
                                )}
                                {contact.email && (
                                  <HStack gap={1}>
                                    <Mail size={12} color="var(--chakra-colors-gray-400)" />
                                    <Text fontSize="xs">{contact.email}</Text>
                                  </HStack>
                                )}
                                {(contact.phone || contact.mobile) && (
                                  <HStack gap={1}>
                                    <Phone size={12} color="var(--chakra-colors-gray-400)" />
                                    <Text fontSize="xs">
                                      {contact.phone}
                                      {contact.phone && contact.mobile && ' / '}
                                      {contact.mobile}
                                    </Text>
                                  </HStack>
                                )}
                              </VStack>
                            </Box>
                          ))}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Notes */}
                {supplier.notes && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <FileText size={16} />
                          <Heading size="sm">Notes</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {supplier.notes}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Historique */}
                <Box px={6} pt={4} pb={6}>
                  <Card.Root variant="outline">
                    <Card.Header py={3}>
                      <Heading size="sm">Historique</Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        <HStack gap={3}>
                          <Box color="gray.400">
                            <Clock size={16} />
                          </Box>
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              Créé le
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {formatDateTime(supplier.created_at)}
                            </Text>
                          </VStack>
                        </HStack>

                        {supplier.updated_at && supplier.updated_at !== supplier.created_at && (
                          <HStack gap={3}>
                            <Box color="blue.500">
                              <Clock size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                Modifié le
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDateTime(supplier.updated_at)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
